import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

// Animated neural-network background — feedforward layout (input → hidden →
// output) with signals that cascade left-to-right, activating each neuron as
// they arrive. Canvas-rendered for perf. Honors prefers-reduced-motion (one
// static frame, no rAF loop). Drop into any positioned (relative) parent —
// fills its bounding box absolutely.
function NeuralNet() {
  const canvasRef = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const parent = canvas.parentElement;

    const PULSE_TRAVEL_MS = 650;
    const SPAWN_INTERVAL_MS = 280;
    const FORWARD_PROB = 0.78;
    const ACTIVATION_FADE_MS = 700;

    let layerSizes = [];
    let layerOffsets = [];
    let width = 0;
    let height = 0;
    let dpr = 1;
    let raf = 0;
    let stopped = false;
    let nextSpawnAt = 0;
    const nodes = [];
    const pulses = [];

    function pickLayerSizes(w) {
      // Wide encoder-decoder shape on desktop; tighter on mobile so neurons
      // don't crush together vertically.
      if (w < 640) return [3, 6, 8, 6, 3];
      if (w < 1024) return [4, 8, 10, 8, 4];
      return [5, 9, 12, 9, 5];
    }

    function resize() {
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function init() {
      nodes.length = 0;
      pulses.length = 0;
      layerSizes = pickLayerSizes(width);
      layerOffsets = [];
      let acc = 0;
      const xMargin = 0.06;
      const usableW = width * (1 - xMargin * 2);
      const xLeft = width * xMargin;
      for (let layer = 0; layer < layerSizes.length; layer++) {
        layerOffsets.push(acc);
        const size = layerSizes[layer];
        const x = xLeft + (layerSizes.length === 1 ? usableW / 2 : usableW * (layer / (layerSizes.length - 1)));
        for (let i = 0; i < size; i++) {
          const y = height * (i + 1) / (size + 1);
          nodes.push({
            x,
            baseY: y,
            y,
            layer,
            bob: Math.random() * Math.PI * 2,
            activatedAt: 0,
          });
          acc++;
        }
      }
    }

    function neighborsAhead(nodeIdx) {
      const node = nodes[nodeIdx];
      if (node.layer >= layerSizes.length - 1) return null;
      const start = layerOffsets[node.layer + 1];
      return { start, count: layerSizes[node.layer + 1] };
    }

    function spawnInputPulse(now) {
      const inputSize = layerSizes[0];
      const sourceIdx = Math.floor(Math.random() * inputSize);
      const next = neighborsAhead(sourceIdx);
      if (!next) return;
      const targetIdx = next.start + Math.floor(Math.random() * next.count);
      pulses.push({ source: sourceIdx, target: targetIdx, start: now });
      nodes[sourceIdx].activatedAt = now;
    }

    function draw(now) {
      if (stopped) return;
      ctx.clearRect(0, 0, width, height);

      for (const n of nodes) {
        n.bob += 0.011;
        n.y = n.baseY + Math.sin(n.bob) * 3.5;
      }

      ctx.lineWidth = 1;
      for (let l = 0; l < layerSizes.length - 1; l++) {
        const aStart = layerOffsets[l];
        const aSize = layerSizes[l];
        const bStart = layerOffsets[l + 1];
        const bSize = layerSizes[l + 1];
        for (let i = 0; i < aSize; i++) {
          const a = nodes[aStart + i];
          for (let j = 0; j < bSize; j++) {
            const b = nodes[bStart + j];
            ctx.strokeStyle = 'rgba(99, 102, 241, 0.09)';
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      if (now >= nextSpawnAt) {
        spawnInputPulse(now);
        nextSpawnAt = now + SPAWN_INTERVAL_MS + Math.random() * SPAWN_INTERVAL_MS;
      }

      for (let p = pulses.length - 1; p >= 0; p--) {
        const pulse = pulses[p];
        const t = (now - pulse.start) / PULSE_TRAVEL_MS;
        if (t >= 1) {
          nodes[pulse.target].activatedAt = now;
          const next = neighborsAhead(pulse.target);
          if (next && Math.random() < FORWARD_PROB) {
            const newTarget = next.start + Math.floor(Math.random() * next.count);
            pulse.source = pulse.target;
            pulse.target = newTarget;
            pulse.start = now;
          } else {
            pulses.splice(p, 1);
          }
          continue;
        }
        const a = nodes[pulse.source];
        const b = nodes[pulse.target];

        const env = Math.sin(t * Math.PI);
        ctx.strokeStyle = `rgba(99, 102, 241, ${0.22 * env})`;
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.lineWidth = 1;

        const x = a.x + (b.x - a.x) * t;
        const y = a.y + (b.y - a.y) * t;
        ctx.fillStyle = `rgba(168, 85, 247, ${0.55 * env})`;
        ctx.shadowColor = `rgba(168, 85, 247, ${0.6 * env})`;
        ctx.shadowBlur = 9;
        ctx.beginPath();
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      for (const n of nodes) {
        const since = now - n.activatedAt;
        const activation = n.activatedAt && since < ACTIVATION_FADE_MS
          ? 1 - since / ACTIVATION_FADE_MS
          : 0;

        const haloR = 5 + activation * 6;
        ctx.fillStyle = `rgba(6, 182, 212, ${0.05 + activation * 0.18})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, haloR, 0, Math.PI * 2);
        ctx.fill();

        const coreR = 2.2 + activation * 1.3;
        ctx.fillStyle = `rgba(6, 182, 212, ${0.38 + activation * 0.22})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, coreR, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    }

    resize();
    init();

    if (reduce) {
      draw(0);
    } else {
      nextSpawnAt = performance.now() + 120;
      raf = requestAnimationFrame(draw);
    }

    let resizeRaf = 0;
    const onResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resize();
        init();
        nextSpawnAt = performance.now() + 120;
      });
    };
    window.addEventListener('resize', onResize);

    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else if (!reduce && !stopped) {
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduce]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}

export default NeuralNet;
